import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { IconArrowLeft } from '@tabler/icons-react';
import Btn from '../components/ui/Btn';
import McpTab from '../components/admin/McpTab';
import MistralUsageTab from '../components/admin/MistralUsageTab';
import OcrQueueTab from '../components/admin/OcrQueueTab';
import { api, ApiError } from '../lib/api';
import { useAuthStore } from '../stores/auth';
import { toast } from '../stores/toaster';

/**
 * Page admin cabinet (réservée admin_cabinet / super_admin).
 *
 * 6 onglets :
 *   - Système : versions, migration head, OCR engine, charter active,
 *               counts, coût IA cumul tenant
 *   - Utilisateurs : list, enable/disable, reset MFA, force password reset
 *   - Invitations : externes (cross-dossier)
 *   - Externals : comptes externes accepteurs
 *   - Audit : 200 derniers événements
 *   - Chartes : versions confidentialité + acceptances count
 */

const ADMIN_ROLES = ['admin_cabinet', 'super_admin'];

const TABS = [
  { id: 'system', label: 'Système' },
  { id: 'users', label: 'Utilisateurs' },
  { id: 'invitations', label: 'Invitations' },
  { id: 'externals', label: 'Externals' },
  { id: 'audit', label: 'Audit' },
  { id: 'charters', label: 'Chartes' },
  { id: 'corpus', label: 'Corpus' },
  { id: 'mcp', label: 'MCP Bridge' },
  { id: 'mistral', label: 'OCR Mistral' },
  { id: 'ocr-queue', label: 'OCR Queue' },
] as const;

type TabId = (typeof TABS)[number]['id'];

export default function AdminPage() {
  const user = useAuthStore((s) => s.user);
  const [tab, setTab] = useState<TabId>('system');

  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (!ADMIN_ROLES.includes(user.role)) {
    return (
      <div className="p-8 text-center text-text-secondary">
        Accès refusé — rôle <span className="font-mono">admin_cabinet</span> ou{' '}
        <span className="font-mono">super_admin</span> requis (rôle actuel :{' '}
        <span className="font-mono">{user.role}</span>).
      </div>
    );
  }

  return (
    <div className="px-6 py-5">
      <Link
        to="/"
        className="mb-3 inline-flex items-center gap-1 text-[12.5px] text-text-secondary hover:text-text-primary"
      >
        <IconArrowLeft size={14} stroke={1.6} />
        Retour aux dossiers
      </Link>
      <header className="mb-5">
        <h1 className="text-[20px] font-medium text-text-primary">Administration</h1>
        <p className="text-[13px] text-text-secondary">
          Tableau de bord cabinet — utilisateurs, invitations, audit, état système.
        </p>
      </header>

      <nav className="mb-5 flex gap-1 border-b border-rule">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={
              'px-3 py-2 text-sm font-medium transition border-b-2 -mb-px ' +
              (tab === t.id
                ? 'border-accent text-text-primary'
                : 'border-transparent text-text-secondary hover:text-text-primary')
            }
          >
            {t.label}
          </button>
        ))}
      </nav>

      <main>
        {tab === 'system' && <SystemTab />}
        {tab === 'users' && <UsersTab />}
        {tab === 'invitations' && <InvitationsTab />}
        {tab === 'externals' && <ExternalsTab />}
        {tab === 'audit' && <AuditTab />}
        {tab === 'charters' && <ChartersTab />}
        {tab === 'corpus' && <CorpusTab />}
        {tab === 'mcp' && <McpTab />}
        {tab === 'mistral' && <MistralUsageTab />}
        {tab === 'ocr-queue' && <OcrQueueTab />}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function useLoad<T>(
  path: string,
  deps: unknown[] = []
): {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<T>(path)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (!cancelled) {
          const detail =
            err instanceof ApiError ? err.problem.detail || err.problem.title : String(err);
          setError(detail);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, nonce, ...deps]);

  return { data, loading, error, reload: () => setNonce((n) => n + 1) };
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('fr-BE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fmtEur(eur: number): string {
  return new Intl.NumberFormat('fr-BE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(eur);
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    active: 'bg-[#dcfce7] text-[#166534]',
    pending: 'bg-[#fef3c7] text-[#92400e]',
    disabled: 'bg-[#fee2e2] text-[#991b1b]',
    pending_setup: 'bg-[#fef3c7] text-[#92400e]',
    accepted: 'bg-[#dcfce7] text-[#166534]',
    account_created: 'bg-[#dbeafe] text-[#1e40af]',
    revoked: 'bg-[#fee2e2] text-[#991b1b]',
    expired: 'bg-paper-subtle text-text-secondary',
  };
  const cls = map[status] || 'bg-paper-subtle text-text-secondary';
  return (
    <span className={`rounded-pill px-2 py-[1px] text-[10px] uppercase tracking-wide ${cls}`}>
      {status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// System tab
// ---------------------------------------------------------------------------

type SystemInfo = {
  build: { git_sha: string; git_branch: string; built_at: string };
  migration_head: string | null;
  runtime: { ocr_primary_engine: string; vault_enabled: boolean; app_env: string };
  charter: { version: string; is_current: boolean; effective_from: string } | null;
  counts: {
    users: Record<string, number>;
    users_by_role: Record<string, number>;
    dossiers: number;
    pdfs: number;
    invitations: Record<string, number>;
    external_users: Record<string, number>;
    page_embeddings: number | null;
  };
  ai_cost_cumul: {
    anthropic: {
      calls: number;
      input_tokens: number;
      output_tokens: number;
      cache_creation_tokens: number;
      cache_read_tokens: number;
      usd: number;
      eur: number;
    };
    voyage: { chars: number; usd: number; eur: number };
    total_eur: number;
    eur_usd_rate: number;
  };
};

function SystemTab() {
  const { data, loading, error, reload } = useLoad<SystemInfo>('/admin/system');

  if (loading) return <div className="text-text-secondary">Chargement…</div>;
  if (error) return <div className="text-[#991b1b]">Erreur : {error}</div>;
  if (!data) return null;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-medium">État système</h2>
        <Btn variant="secondary" size="sm" onClick={reload}>
          ↻ Rafraîchir
        </Btn>
      </div>

      <Card title="Build">
        <KV k="Git SHA" v={<code className="font-mono">{data.build.git_sha}</code>} />
        <KV k="Branch" v={data.build.git_branch} />
        <KV k="Built at" v={fmtDate(data.build.built_at)} />
        <KV
          k="Migration head"
          v={<code className="font-mono">{data.migration_head || '—'}</code>}
        />
      </Card>

      <Card title="Runtime">
        <KV k="Environnement" v={data.runtime.app_env} />
        <KV
          k="OCR engine"
          v={
            <span
              className={
                data.runtime.ocr_primary_engine === 'paddleocr' ? '' : 'text-status-warning-text'
              }
            >
              {data.runtime.ocr_primary_engine}
              {data.runtime.ocr_primary_engine !== 'paddleocr' && (
                <span className="ml-2 text-[11px]">(fallback — Paddle indisponible)</span>
              )}
            </span>
          }
        />
        <KV
          k="Vault"
          v={
            data.runtime.vault_enabled ? (
              'activé'
            ) : (
              <span className="text-text-secondary">désactivé (secrets en .env)</span>
            )
          }
        />
      </Card>

      <Card title="Charte de confidentialité">
        {data.charter ? (
          <>
            <KV k="Version active" v={<code className="font-mono">{data.charter.version}</code>} />
            <KV k="Effective depuis" v={fmtDate(data.charter.effective_from)} />
          </>
        ) : (
          <p className="text-status-warning-text text-sm">
            ⚠ Aucune charte active. Les invitations externes sont <strong>bloquées côté API</strong>{' '}
            jusqu'à activation manuelle (cf.{' '}
            <code className="font-mono">docs/charters/CHARTER_v1.0.md</code>).
          </p>
        )}
      </Card>

      <Card title="Statistiques">
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 md:grid-cols-3">
          <Stat label="Dossiers" value={data.counts.dossiers} />
          <Stat label="PDFs" value={data.counts.pdfs} />
          <Stat label="Embeddings (pages)" value={data.counts.page_embeddings ?? '—'} />
        </div>
        <div className="mt-3">
          <p className="text-[11px] uppercase tracking-wide text-text-secondary">
            Utilisateurs par statut
          </p>
          <CountTable counts={data.counts.users} />
        </div>
        <div className="mt-3">
          <p className="text-[11px] uppercase tracking-wide text-text-secondary">
            Utilisateurs par rôle
          </p>
          <CountTable counts={data.counts.users_by_role} />
        </div>
        <div className="mt-3">
          <p className="text-[11px] uppercase tracking-wide text-text-secondary">Invitations</p>
          <CountTable counts={data.counts.invitations} />
        </div>
        <div className="mt-3">
          <p className="text-[11px] uppercase tracking-wide text-text-secondary">
            Comptes externes
          </p>
          <CountTable counts={data.counts.external_users} />
        </div>
      </Card>

      <Card title="Coût IA cumulé (tenant)">
        <div className="mb-3 text-[20px] font-medium text-text-primary">
          {fmtEur(data.ai_cost_cumul.total_eur)}
        </div>
        <p className="text-[11px] text-text-secondary mb-3">
          Taux EUR/USD : {data.ai_cost_cumul.eur_usd_rate}
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-md border border-rule p-3">
            <p className="text-[11px] uppercase tracking-wide text-text-secondary">
              Anthropic (Sonnet 4.6)
            </p>
            <p className="text-base font-medium">{fmtEur(data.ai_cost_cumul.anthropic.eur)}</p>
            <p className="text-[11px] text-text-secondary mt-1">
              {data.ai_cost_cumul.anthropic.calls} appels · in{' '}
              {data.ai_cost_cumul.anthropic.input_tokens.toLocaleString('fr-BE')} · out{' '}
              {data.ai_cost_cumul.anthropic.output_tokens.toLocaleString('fr-BE')}
              {data.ai_cost_cumul.anthropic.cache_read_tokens > 0 && (
                <>
                  {' '}
                  · cache-read{' '}
                  {data.ai_cost_cumul.anthropic.cache_read_tokens.toLocaleString('fr-BE')}
                </>
              )}
            </p>
          </div>
          <div className="rounded-md border border-rule p-3">
            <p className="text-[11px] uppercase tracking-wide text-text-secondary">
              Voyage (Law-2 embeddings)
            </p>
            <p className="text-base font-medium">{fmtEur(data.ai_cost_cumul.voyage.eur)}</p>
            <p className="text-[11px] text-text-secondary mt-1">
              {data.ai_cost_cumul.voyage.chars.toLocaleString('fr-BE')} chars indexés
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-md border border-rule bg-paper-elevated p-4">
      <h3 className="mb-2 text-[13px] font-medium uppercase tracking-wide text-text-secondary">
        {title}
      </h3>
      <div className="text-sm">{children}</div>
    </section>
  );
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex justify-between border-b border-rule/50 py-1 last:border-b-0">
      <span className="text-text-secondary">{k}</span>
      <span className="text-text-primary">{v}</span>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-text-secondary">{label}</p>
      <p className="text-xl font-medium text-text-primary">{value}</p>
    </div>
  );
}

function CountTable({ counts }: { counts: Record<string, number> }) {
  const entries = Object.entries(counts);
  if (entries.length === 0) {
    return <p className="text-[12px] text-text-secondary">(vide)</p>;
  }
  return (
    <div className="flex flex-wrap gap-2 mt-1">
      {entries.map(([k, v]) => (
        <span key={k} className="rounded-pill bg-paper-subtle px-2 py-[2px] text-[12px]">
          <span className="text-text-secondary">{k}</span>{' '}
          <span className="font-medium text-text-primary">{v}</span>
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Users tab
// ---------------------------------------------------------------------------

type AdminUser = {
  id: string;
  email: string;
  display_name: string;
  role: string;
  status: 'active' | 'disabled' | 'pending';
  mfa_enabled: boolean;
  must_change_password: boolean;
  last_login_at: string | null;
  last_login_ip: string | null;
  password_changed_at: string;
  created_at: string;
};

function UsersTab() {
  const { data, loading, error, reload } = useLoad<AdminUser[]>('/admin/users');
  const currentUserId = useAuthStore((s) => s.user?.id);
  const currentRole = useAuthStore((s) => s.user?.role);
  const [createOpen, setCreateOpen] = useState(false);

  const doAction = async (
    u: AdminUser,
    op: 'disable' | 'enable' | 'reset-mfa' | 'force-password-reset',
    label: string
  ) => {
    if (!window.confirm(`${label} pour ${u.email} ?`)) return;
    try {
      await api.post(`/admin/users/${u.id}/${op}`);
      toast.success(`${label} effectué pour ${u.email}.`);
      reload();
    } catch (err) {
      const detail =
        err instanceof ApiError ? err.problem.detail || err.problem.title : String(err);
      toast.error(`Échec : ${detail}`);
    }
  };

  if (loading) return <div className="text-text-secondary">Chargement…</div>;
  if (error) return <div className="text-[#991b1b]">Erreur : {error}</div>;
  if (!data) return null;

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-medium">
          {data.length} utilisateur{data.length > 1 ? 's' : ''}
        </h2>
        <div className="flex items-center gap-2">
          <Btn variant="primary" size="sm" onClick={() => setCreateOpen(true)}>
            + Nouveau membre
          </Btn>
          <Btn variant="secondary" size="sm" onClick={reload}>
            ↻
          </Btn>
        </div>
      </div>
      {createOpen && (
        <CreateUserModal
          canCreateSuperAdmin={currentRole === 'super_admin'}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            reload();
          }}
        />
      )}
      <div className="overflow-x-auto rounded-md border border-rule">
        <table className="w-full text-sm">
          <thead className="bg-paper-subtle text-[11px] uppercase tracking-wide text-text-secondary">
            <tr>
              <th className="px-3 py-2 text-left">Utilisateur</th>
              <th className="px-3 py-2 text-left">Rôle</th>
              <th className="px-3 py-2 text-left">Statut</th>
              <th className="px-3 py-2 text-left">MFA</th>
              <th className="px-3 py-2 text-left">Dernière connexion</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.map((u) => (
              <tr key={u.id} className="border-t border-rule/50">
                <td className="px-3 py-2">
                  <div className="font-medium">{u.display_name}</div>
                  <div className="text-[11px] text-text-secondary">{u.email}</div>
                </td>
                <td className="px-3 py-2 font-mono text-[11px]">{u.role}</td>
                <td className="px-3 py-2">
                  <StatusPill status={u.status} />
                  {u.must_change_password && (
                    <span className="ml-2 rounded-pill bg-status-warning-bg px-2 py-[1px] text-[10px] text-status-warning-text">
                      pw à changer
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-text-secondary">
                  {u.mfa_enabled ? '✓ activé' : '—'}
                </td>
                <td className="px-3 py-2 text-text-secondary text-[12px]">
                  {fmtDate(u.last_login_at)}
                  {u.last_login_ip && (
                    <div className="text-[10px] font-mono">{u.last_login_ip}</div>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex flex-wrap justify-end gap-1">
                    {u.id !== currentUserId &&
                      (u.status === 'active' ? (
                        <Btn
                          size="sm"
                          variant="secondary"
                          onClick={() => doAction(u, 'disable', 'Désactiver')}
                        >
                          Désactiver
                        </Btn>
                      ) : (
                        <Btn size="sm" onClick={() => doAction(u, 'enable', 'Activer')}>
                          Activer
                        </Btn>
                      ))}
                    {u.mfa_enabled && (
                      <Btn
                        size="sm"
                        variant="secondary"
                        onClick={() => doAction(u, 'reset-mfa', 'Réinitialiser MFA')}
                      >
                        Reset MFA
                      </Btn>
                    )}
                    {!u.must_change_password && (
                      <Btn
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          doAction(u, 'force-password-reset', 'Forcer changement de mot de passe')
                        }
                      >
                        Forcer pw
                      </Btn>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CreateUserModal — nouveau membre du cabinet (mode direct ou invitation)
// ---------------------------------------------------------------------------

type CreateMode = 'direct' | 'invite';

const ROLE_OPTIONS: { value: string; label: string; hint: string }[] = [
  { value: 'avocat', label: 'Avocat', hint: 'Accès complet aux dossiers du cabinet' },
  { value: 'stagiaire', label: 'Stagiaire', hint: 'Comme avocat, lecture/écriture sur ses dossiers' },
  { value: 'secretaire', label: 'Secrétaire', hint: 'Gestion administrative (upload, classement)' },
  { value: 'admin_cabinet', label: 'Admin cabinet', hint: 'Accès au tableau /admin (users, audit, etc.)' },
];

type CreateResponse = {
  id: string;
  email: string;
  display_name: string;
  role: string;
  status: string;
  mode: CreateMode;
  invite_sent: boolean;
  temp_password?: string;
};

function CreateUserModal({
  canCreateSuperAdmin,
  onClose,
  onCreated,
}: {
  canCreateSuperAdmin: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState('avocat');
  const [mode, setMode] = useState<CreateMode>('invite');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<CreateResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const roleOptions = useMemo(
    () =>
      canCreateSuperAdmin
        ? [...ROLE_OPTIONS, { value: 'super_admin', label: 'Super admin', hint: 'Plein pouvoir, à éviter sauf cas exceptionnel' }]
        : ROLE_OPTIONS,
    [canCreateSuperAdmin]
  );

  const submit = async () => {
    if (!email.trim() || !displayName.trim()) {
      toast.error('Email et nom complet requis.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post<CreateResponse>('/admin/users', {
        email: email.trim().toLowerCase(),
        display_name: displayName.trim(),
        role,
        mode,
      });
      setResult(res);
      toast.success(`Compte créé pour ${res.email}.`);
    } catch (err) {
      const detail =
        err instanceof ApiError ? err.problem.detail || err.problem.title : String(err);
      toast.error(`Échec : ${detail}`);
    } finally {
      setSubmitting(false);
    }
  };

  const copyPwd = async () => {
    if (!result?.temp_password) return;
    try {
      await navigator.clipboard.writeText(result.temp_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Impossible de copier — sélectionnez manuellement.');
    }
  };

  const closeAfterSuccess = () => {
    onCreated();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-lg flex-col rounded-md bg-paper-elevated shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between border-b border-rule px-5 py-3">
          <div>
            <h2 className="font-serif text-[18px] font-medium text-text-primary">
              {result ? 'Compte créé' : 'Nouveau membre du cabinet'}
            </h2>
            {!result && (
              <p className="mt-0.5 text-[12px] text-text-secondary">
                Crée un compte interne (User), pas un invité externe. Le mot de passe sera temporaire
                et devra être changé à la première connexion.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-text-tertiary hover:bg-paper-card hover:text-text-primary"
            aria-label="Fermer"
          >
            ✕
          </button>
        </header>

        {result ? (
          <div className="px-5 py-4">
            <div className="mb-3 rounded border border-rule bg-paper-card p-3 text-[13px]">
              <div>
                <span className="text-text-secondary">Email :</span>{' '}
                <span className="font-medium">{result.email}</span>
              </div>
              <div>
                <span className="text-text-secondary">Nom :</span> {result.display_name}
              </div>
              <div>
                <span className="text-text-secondary">Rôle :</span>{' '}
                <span className="font-mono">{result.role}</span>
              </div>
              <div>
                <span className="text-text-secondary">Mode :</span>{' '}
                <span className="font-mono">{result.mode}</span>
                {result.mode === 'invite' && (
                  <span
                    className={`ml-2 rounded-pill px-2 py-[1px] text-[10px] ${
                      result.invite_sent
                        ? 'bg-status-success-bg text-status-success-text'
                        : 'bg-status-warning-bg text-status-warning-text'
                    }`}
                  >
                    {result.invite_sent ? '✓ email envoyé' : '⚠ email NON envoyé'}
                  </span>
                )}
              </div>
            </div>
            {result.temp_password && (
              <div className="mb-3 rounded border-2 border-status-warning-text/40 bg-status-warning-bg/50 p-3">
                <p className="mb-2 text-[12px] text-status-warning-text">
                  {result.mode === 'direct' ? (
                    <>
                      <strong>Mot de passe temporaire à transmettre maintenant</strong> — il ne sera
                      plus jamais affiché. Le membre devra le changer à la première connexion.
                    </>
                  ) : (
                    <>
                      <strong>Brevo n'a pas envoyé le mail</strong> — voici le mot de passe temporaire
                      à transmettre manuellement (canal sécurisé).
                    </>
                  )}
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 rounded bg-paper-elevated px-3 py-2 font-mono text-[14px] break-all">
                    {result.temp_password}
                  </code>
                  <Btn size="sm" variant="secondary" onClick={() => void copyPwd()}>
                    {copied ? '✓ copié' : 'Copier'}
                  </Btn>
                </div>
              </div>
            )}
            {!result.temp_password && result.mode === 'invite' && result.invite_sent && (
              <p className="mb-3 text-[12px] text-text-secondary">
                Le mot de passe temporaire a été envoyé directement à {result.email} par Brevo.
                Il n'est pas affiché ici (sécurité).
              </p>
            )}
            <div className="flex justify-end">
              <Btn variant="primary" onClick={closeAfterSuccess}>
                Terminer
              </Btn>
            </div>
          </div>
        ) : (
          <div className="px-5 py-4 space-y-3">
            <div>
              <label className="mb-1 block text-[12px] font-medium text-text-secondary">
                Adresse email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="prenom.nom@m24-avocats.be"
                className="w-full rounded border border-rule bg-paper-elevated px-3 py-2 text-[14px]"
                autoFocus
                disabled={submitting}
              />
            </div>
            <div>
              <label className="mb-1 block text-[12px] font-medium text-text-secondary">
                Nom complet
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Prénom Nom"
                className="w-full rounded border border-rule bg-paper-elevated px-3 py-2 text-[14px]"
                disabled={submitting}
              />
            </div>
            <div>
              <label className="mb-1 block text-[12px] font-medium text-text-secondary">Rôle</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full rounded border border-rule bg-paper-elevated px-3 py-2 text-[14px]"
                disabled={submitting}
              >
                {roleOptions.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label} — {r.hint}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[12px] font-medium text-text-secondary">
                Transmission du mot de passe temporaire
              </label>
              <div className="space-y-2">
                <label className="flex items-start gap-2 cursor-pointer rounded border border-rule bg-paper-card p-2.5 hover:border-rule-strong">
                  <input
                    type="radio"
                    name="mode"
                    value="invite"
                    checked={mode === 'invite'}
                    onChange={() => setMode('invite')}
                    disabled={submitting}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="text-[13px] font-medium">Envoi par email (recommandé)</div>
                    <div className="text-[11.5px] text-text-secondary">
                      Le membre reçoit un mail Brevo avec ses identifiants et le lien de connexion.
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer rounded border border-rule bg-paper-card p-2.5 hover:border-rule-strong">
                  <input
                    type="radio"
                    name="mode"
                    value="direct"
                    checked={mode === 'direct'}
                    onChange={() => setMode('direct')}
                    disabled={submitting}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="text-[13px] font-medium">Affichage à l'écran</div>
                    <div className="text-[11.5px] text-text-secondary">
                      Le mot de passe s'affiche une seule fois ; à toi de le transmettre.
                    </div>
                  </div>
                </label>
              </div>
            </div>
            <footer className="flex items-center justify-end gap-2 pt-2">
              <Btn type="button" variant="ghost" onClick={onClose} disabled={submitting}>
                Annuler
              </Btn>
              <Btn type="button" variant="primary" onClick={() => void submit()} disabled={submitting}>
                {submitting ? 'Création…' : 'Créer le compte'}
              </Btn>
            </footer>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Invitations tab
// ---------------------------------------------------------------------------

type AdminInvitation = {
  id: string;
  dossier_id: string;
  dossier_label: string;
  invited_by_display_name: string;
  invited_email: string;
  invited_display_name: string;
  invitation_type: string;
  status: string;
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  created_at: string;
};

function InvitationsTab() {
  const { data, loading, error } = useLoad<AdminInvitation[]>('/admin/invitations');

  if (loading) return <div className="text-text-secondary">Chargement…</div>;
  if (error) return <div className="text-[#991b1b]">Erreur : {error}</div>;
  if (!data) return null;

  return (
    <div>
      <h2 className="mb-3 text-base font-medium">
        {data.length} invitation{data.length > 1 ? 's' : ''}
      </h2>
      {data.length === 0 ? (
        <p className="text-text-secondary">Aucune invitation émise.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-rule">
          <table className="w-full text-sm">
            <thead className="bg-paper-subtle text-[11px] uppercase tracking-wide text-text-secondary">
              <tr>
                <th className="px-3 py-2 text-left">Invité</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Dossier</th>
                <th className="px-3 py-2 text-left">Émetteur</th>
                <th className="px-3 py-2 text-left">Statut</th>
                <th className="px-3 py-2 text-left">Expire</th>
                <th className="px-3 py-2 text-left">Créée</th>
              </tr>
            </thead>
            <tbody>
              {data.map((inv) => (
                <tr key={inv.id} className="border-t border-rule/50">
                  <td className="px-3 py-2">
                    <div className="font-medium">{inv.invited_display_name}</div>
                    <div className="text-[11px] text-text-secondary">{inv.invited_email}</div>
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px]">{inv.invitation_type}</td>
                  <td className="px-3 py-2 text-[12px]">{inv.dossier_label}</td>
                  <td className="px-3 py-2 text-[12px]">{inv.invited_by_display_name}</td>
                  <td className="px-3 py-2">
                    <StatusPill status={inv.status} />
                  </td>
                  <td className="px-3 py-2 text-[12px] text-text-secondary">
                    {fmtDate(inv.expires_at)}
                  </td>
                  <td className="px-3 py-2 text-[12px] text-text-secondary">
                    {fmtDate(inv.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Externals tab
// ---------------------------------------------------------------------------

type AdminExternalUser = {
  id: string;
  email: string;
  display_name: string;
  status: string;
  mfa_enabled: boolean;
  last_login_at: string | null;
  last_login_ip: string | null;
  created_at: string;
  invitation_id: string;
  invitation_type: string;
  dossier_id: string;
  dossier_label: string;
};

function ExternalsTab() {
  const { data, loading, error } = useLoad<AdminExternalUser[]>('/admin/external-users');

  if (loading) return <div className="text-text-secondary">Chargement…</div>;
  if (error) return <div className="text-[#991b1b]">Erreur : {error}</div>;
  if (!data) return null;

  return (
    <div>
      <h2 className="mb-3 text-base font-medium">
        {data.length} compte{data.length > 1 ? 's' : ''} externe{data.length > 1 ? 's' : ''}
      </h2>
      {data.length === 0 ? (
        <p className="text-text-secondary">Aucun compte externe créé.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-rule">
          <table className="w-full text-sm">
            <thead className="bg-paper-subtle text-[11px] uppercase tracking-wide text-text-secondary">
              <tr>
                <th className="px-3 py-2 text-left">Compte</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Dossier</th>
                <th className="px-3 py-2 text-left">Statut</th>
                <th className="px-3 py-2 text-left">MFA</th>
                <th className="px-3 py-2 text-left">Dernière connexion</th>
              </tr>
            </thead>
            <tbody>
              {data.map((ext) => (
                <tr key={ext.id} className="border-t border-rule/50">
                  <td className="px-3 py-2">
                    <div className="font-medium">{ext.display_name}</div>
                    <div className="text-[11px] text-text-secondary">{ext.email}</div>
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px]">{ext.invitation_type}</td>
                  <td className="px-3 py-2 text-[12px]">{ext.dossier_label}</td>
                  <td className="px-3 py-2">
                    <StatusPill status={ext.status} />
                  </td>
                  <td className="px-3 py-2 text-text-secondary">{ext.mfa_enabled ? '✓' : '—'}</td>
                  <td className="px-3 py-2 text-[12px] text-text-secondary">
                    {fmtDate(ext.last_login_at)}
                    {ext.last_login_ip && (
                      <div className="text-[10px] font-mono">{ext.last_login_ip}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Audit tab
// ---------------------------------------------------------------------------

type AdminAuditEntry = {
  id: number;
  created_at: string;
  user_id: string | null;
  user_display_name: string | null;
  user_email: string | null;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  dossier_id: string | null;
  metadata: Record<string, unknown> | null;
  ip_address: string | null;
};

function AuditTab() {
  const [limit, setLimit] = useState(200);
  const { data, loading, error, reload } = useLoad<AdminAuditEntry[]>(
    `/admin/audit?limit=${limit}`,
    [limit]
  );
  const [actionFilter, setActionFilter] = useState('');

  const filtered = useMemo(() => {
    if (!data) return [];
    if (!actionFilter) return data;
    return data.filter((r) => r.action.includes(actionFilter));
  }, [data, actionFilter]);

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-base font-medium">Audit log</h2>
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="filtre action (chat.message, admin.*, …)"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="rounded-md border border-rule bg-paper-elevated px-2 py-1 text-sm w-64"
          />
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="rounded-md border border-rule bg-paper-elevated px-2 py-1 text-sm"
          >
            <option value={50}>50</option>
            <option value={200}>200</option>
            <option value={500}>500</option>
            <option value={1000}>1000</option>
          </select>
          <Btn variant="secondary" size="sm" onClick={reload}>
            ↻
          </Btn>
        </div>
      </div>

      {loading && <div className="text-text-secondary">Chargement…</div>}
      {error && <div className="text-[#991b1b]">Erreur : {error}</div>}
      {!loading && !error && (
        <div className="overflow-x-auto rounded-md border border-rule">
          <table className="w-full text-sm">
            <thead className="bg-paper-subtle text-[11px] uppercase tracking-wide text-text-secondary">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Action</th>
                <th className="px-3 py-2 text-left">Utilisateur</th>
                <th className="px-3 py-2 text-left">Ressource</th>
                <th className="px-3 py-2 text-left">IP</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-rule/50">
                  <td className="px-3 py-2 text-[11px] text-text-secondary">
                    {fmtDate(r.created_at)}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px]">{r.action}</td>
                  <td className="px-3 py-2 text-[12px]">
                    {r.user_display_name || <span className="text-text-secondary">système</span>}
                    {r.user_email && (
                      <div className="text-[10px] text-text-secondary">{r.user_email}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-text-secondary">
                    {r.resource_type && <code>{r.resource_type}</code>}
                    {r.resource_id && (
                      <code className="ml-1 text-[10px]">{r.resource_id.slice(0, 8)}</code>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[10px] font-mono text-text-secondary">
                    {r.ip_address || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Charters tab
// ---------------------------------------------------------------------------

type AdminCharter = {
  version: string;
  is_current: boolean;
  effective_from: string;
  content_hash: string;
  content_length: number;
  acceptances_count: number;
  created_at: string;
};

function ChartersTab() {
  const { data, loading, error } = useLoad<AdminCharter[]>('/admin/charters');

  if (loading) return <div className="text-text-secondary">Chargement…</div>;
  if (error) return <div className="text-[#991b1b]">Erreur : {error}</div>;
  if (!data) return null;

  return (
    <div>
      <h2 className="mb-3 text-base font-medium">Chartes de confidentialité</h2>
      {data.length === 0 ? (
        <div className="rounded-md border border-status-warning-text/40 bg-status-warning-bg p-4 text-sm">
          <p className="font-medium">⚠ Aucune charte en base.</p>
          <p className="mt-1 text-text-secondary">
            Les invitations externes sont bloquées tant qu'aucune charte
            <code className="mx-1 font-mono">is_current=true</code>
            n'est insérée. Le brouillon vit dans
            <code className="mx-1 font-mono">docs/charters/CHARTER_v1.0.md</code> — à relire et
            amender par M24 avant activation manuelle (SQL).
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-rule">
          <table className="w-full text-sm">
            <thead className="bg-paper-subtle text-[11px] uppercase tracking-wide text-text-secondary">
              <tr>
                <th className="px-3 py-2 text-left">Version</th>
                <th className="px-3 py-2 text-left">Active</th>
                <th className="px-3 py-2 text-left">Effective depuis</th>
                <th className="px-3 py-2 text-left">Hash</th>
                <th className="px-3 py-2 text-right">Taille</th>
                <th className="px-3 py-2 text-right">Acceptations</th>
              </tr>
            </thead>
            <tbody>
              {data.map((ch) => (
                <tr key={ch.version} className="border-t border-rule/50">
                  <td className="px-3 py-2 font-mono">{ch.version}</td>
                  <td className="px-3 py-2">
                    {ch.is_current ? <StatusPill status="active" /> : '—'}
                  </td>
                  <td className="px-3 py-2 text-[12px]">{fmtDate(ch.effective_from)}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-text-secondary">
                    {ch.content_hash.slice(0, 16)}…
                  </td>
                  <td className="px-3 py-2 text-right text-[12px]">{ch.content_length} chars</td>
                  <td className="px-3 py-2 text-right">{ch.acceptances_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Corpus tab — upload preflight + ingest (admin cabinet, ADR-032)
// ---------------------------------------------------------------------------

type PreflightResult = {
  sha256: string;
  kind: 'pdf' | 'doc' | 'docx';
  filename: string;
  size_bytes: number;
  char_count: number;
  sample_chunks: number;
  estimated_total_chunks: number;
  first_chars_preview: string;
  relevance_score: number;
  interpretation: string;
  top_similar_sources: {
    source_id: string;
    source_type: string;
    title: string;
    author: string | null;
    year: number | null;
    score: number;
  }[];
};

const CORPUS_SOURCE_TYPES = [
  { value: 'doctrine', label: 'Doctrine (ouvrage)' },
  { value: 'jurisprudence_cass', label: 'Jurisprudence Cass.' },
  { value: 'code_legal', label: 'Texte légal (code)' },
  { value: 'act_template', label: "Modèle d'acte" },
];

function CorpusTab() {
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [preflighting, setPreflighting] = useState(false);
  const [preflight, setPreflight] = useState<PreflightResult | null>(null);
  const [ingesting, setIngesting] = useState(false);
  const [sourceType, setSourceType] = useState('doctrine');
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [year, setYear] = useState('');

  const reset = () => {
    setFile(null);
    setPreflight(null);
    setTitle('');
    setAuthor('');
    setYear('');
  };

  const onFile = async (f: File) => {
    setFile(f);
    setPreflight(null);
    setTitle(f.name.replace(/\.[^.]+$/, ''));
    setPreflighting(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await fetch('/api/v1/admin/corpus/preflight', {
        method: 'POST',
        body: fd,
        credentials: 'include',
        headers: {
          Authorization: 'Bearer ' + (useAuthStore.getState().accessToken ?? ''),
        },
      });
      if (!r.ok) throw new Error('preflight ' + r.status);
      const data: PreflightResult = await r.json();
      setPreflight(data);
    } catch (e) {
      toast.error('Preflight KO : ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setPreflighting(false);
    }
  };

  const onIngest = async () => {
    if (!file || !preflight) return;
    if (!title.trim()) {
      toast.error('Titre obligatoire');
      return;
    }
    setIngesting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('source_type', sourceType);
      fd.append('title', title.trim());
      if (author.trim()) fd.append('author', author.trim());
      if (year.trim()) fd.append('year', year.trim());
      const r = await fetch('/api/v1/admin/corpus/ingest', {
        method: 'POST',
        body: fd,
        credentials: 'include',
        headers: {
          Authorization: 'Bearer ' + (useAuthStore.getState().accessToken ?? ''),
        },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.error || data?.detail || 'ingest ' + r.status);
      toast.success(
        data.status === 'already_indexed'
          ? 'Document déjà indexé (sha256 identique)'
          : `Ingéré : ${data.chunks_inserted ?? '?'} chunks`
      );
      reset();
    } catch (e) {
      toast.error('Ingest KO : ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setIngesting(false);
    }
  };

  const gaugeColor = !preflight
    ? 'bg-paper-subtle'
    : preflight.relevance_score >= 70
      ? 'bg-cat-imp'
      : preflight.relevance_score >= 30
        ? 'bg-cat-fav'
        : 'bg-cat-def';

  return (
    <section className="space-y-5">
      <header>
        <h2 className="text-[15px] font-medium text-text-primary">Ingestion corpus</h2>
        <p className="mt-1 text-[12.5px] text-text-secondary">
          Dépose un PDF / DOC / DOCX. Une analyse pré-vol calcule une jauge de pertinence
          (similarité moyenne avec le corpus existant) avant de l'ingérer définitivement. Tu peux
          annuler si la jauge montre « hors-domaine » ou « doublon probable ».
        </p>
      </header>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f) void onFile(f);
        }}
        className={
          'rounded-md border-2 border-dashed p-8 text-center transition ' +
          (dragOver ? 'border-accent bg-accent-light/30' : 'border-rule-strong bg-paper-card')
        }
      >
        {file ? (
          <div className="text-[12.5px]">
            <p className="font-medium text-text-primary">{file.name}</p>
            <p className="mt-0.5 text-[11px] text-text-tertiary">
              {(file.size / 1024).toFixed(0)} KB · {file.type || 'application/octet-stream'}
            </p>
            <button
              type="button"
              onClick={reset}
              className="mt-2 text-[11px] text-accent-text hover:underline"
            >
              Changer
            </button>
          </div>
        ) : (
          <div className="space-y-2 text-[12.5px] text-text-secondary">
            <p>Glisse un fichier ici, ou</p>
            <label className="inline-block cursor-pointer rounded-md border border-rule-strong bg-paper-elevated px-3 py-1.5 text-text-primary hover:bg-paper-subtle">
              Choisir un fichier
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void onFile(f);
                }}
              />
            </label>
            <p className="text-[11px] text-text-tertiary">PDF / DOC / DOCX, max 50 MB</p>
          </div>
        )}
      </div>

      {preflighting && (
        <p className="text-[12.5px] text-text-secondary">
          Analyse preflight en cours (extraction + embedding sample + similarité corpus)…
        </p>
      )}

      {preflight && (
        <div className="rounded-md border border-rule bg-paper-elevated p-4 space-y-4">
          <div>
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-[14px] font-medium text-text-primary">
                Jauge de pertinence : {preflight.relevance_score}/100
              </h3>
              <span
                className={
                  'rounded-pill px-2.5 py-0.5 text-[11px] font-medium ' +
                  (preflight.relevance_score >= 70
                    ? 'bg-cat-imp-pastel text-cat-imp-text'
                    : preflight.relevance_score >= 30
                      ? 'bg-cat-fav-pastel text-cat-fav'
                      : 'bg-cat-def-pastel text-cat-def')
                }
              >
                {preflight.interpretation}
              </span>
            </div>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-paper-subtle">
              <div
                className={'h-full transition-all ' + gaugeColor}
                style={{ width: preflight.relevance_score + '%' }}
              />
            </div>
            <p className="mt-1 text-[11px] text-text-tertiary">
              {preflight.char_count.toLocaleString('fr-BE')} caractères extraits · ~
              {preflight.estimated_total_chunks} chunks attendus · sha256{' '}
              <code>{preflight.sha256.slice(0, 12)}…</code>
            </p>
          </div>

          {preflight.top_similar_sources.length > 0 && (
            <details className="text-[12px]" open={preflight.relevance_score >= 70}>
              <summary className="cursor-pointer font-medium text-text-primary">
                Top documents similaires ({preflight.top_similar_sources.length})
              </summary>
              <ul className="mt-2 space-y-1">
                {preflight.top_similar_sources.map((s) => (
                  <li key={s.source_id} className="flex items-baseline justify-between gap-2">
                    <span className="truncate">
                      <span className="text-[10px] text-text-tertiary">[{s.source_type}]</span>{' '}
                      {s.title}
                      {s.author && <span className="text-text-tertiary"> — {s.author}</span>}
                      {s.year && <span className="text-text-tertiary"> ({s.year})</span>}
                    </span>
                    <span className="font-mono text-[11px] text-text-tertiary shrink-0">
                      {(s.score * 100).toFixed(0)} %
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}

          <details className="text-[12px]">
            <summary className="cursor-pointer font-medium text-text-primary">
              Aperçu texte (500 premiers caractères)
            </summary>
            <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-paper-subtle p-2 text-[11px] font-mono">
              {preflight.first_chars_preview}
            </pre>
          </details>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="text-[12px]">
              Type de source
              <select
                value={sourceType}
                onChange={(e) => setSourceType(e.target.value)}
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-card px-2 py-1.5 text-[13px]"
              >
                {CORPUS_SOURCE_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-[12px]">
              Titre
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-card px-2 py-1.5 text-[13px]"
              />
            </label>
            <label className="text-[12px]">
              Auteur (optionnel)
              <input
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                placeholder="Franchimont, Beernaert…"
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-card px-2 py-1.5 text-[13px]"
              />
            </label>
            <label className="text-[12px]">
              Année (optionnel)
              <input
                value={year}
                onChange={(e) => setYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="2024"
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-card px-2 py-1.5 text-[13px] font-mono"
              />
            </label>
          </div>

          <div className="flex gap-2 justify-end">
            <Btn variant="ghost" size="sm" onClick={reset} disabled={ingesting}>
              Annuler
            </Btn>
            <Btn variant="primary" size="sm" onClick={() => void onIngest()} disabled={ingesting}>
              {ingesting
                ? 'Ingestion…'
                : preflight.relevance_score >= 70
                  ? 'Ingérer quand même (doublon ?)'
                  : preflight.relevance_score < 30
                    ? 'Ingérer malgré la jauge basse'
                    : 'Ingérer dans le corpus'}
            </Btn>
          </div>
        </div>
      )}
    </section>
  );
}
